import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconMessage2 } from '@tabler/icons-react';
import { TeeButton } from '@/components/ui/tee-button';
import { useIntakePanelEntryHidden } from '@/hooks/useHideIntakePanelEntry';
import { IntakePanel } from './IntakePanel';

/**
 * Mounted once in AppShell for every authenticated route. Replaces the
 * retired feedback-to-Linear widget per 408 D-01/D-02: renders the "Report an issue"
 * vertical tab on the right edge (desktop) or a floating "Help" pill above
 * the tab bar (mobile), and the Intake Panel it opens. No permission check —
 * any authenticated box-os admin sees this entry point.
 */
export const IntakeEntryPoint: React.FC = () => {
  const { t } = useTranslation();
  const hidden = useIntakePanelEntryHidden();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {!hidden && !isOpen && (
        <>
          {/* Desktop: vertical tab pinned to the right edge — SAME geometry as retired widget (D-02) */}
          <TeeButton
            type="button"
            onClick={() => setIsOpen(true)}
            aria-label={t('intake.open_label')}
            text={t('intake.tab_label')}
            className="fixed right-0 top-1/2 z-30 hidden max-h-none -translate-y-1/2 rounded-l-[8px] bg-[var(--color-forest)] px-2.5 py-4 text-[14px] font-bold uppercase tracking-[0.14em] text-[var(--text-on-forest)] shadow-[0_4px_14px_rgba(12,61,45,0.22)] hover:bg-[var(--color-forest-deep)] md:block"
            style={{ writingMode: 'vertical-rl', transform: 'translateY(-50%)' }}
          />
          {/* Mobile: floating pill — SAME geometry, bottom offset preserved verbatim */}
          <TeeButton
            type="button"
            onClick={() => setIsOpen(true)}
            aria-label={t('intake.open_label')}
            leftIcon={<IconMessage2 size={18} aria-hidden="true" />}
            text={t('intake.tab_label_mobile')}
            className="fixed right-4 z-30 flex max-h-none items-center gap-2 rounded-full bg-[var(--color-forest)] px-4 py-2.5 text-[14px] font-bold text-[var(--text-on-forest)] shadow-[0_4px_14px_rgba(12,61,45,0.22)] md:hidden"
            style={{ bottom: 'calc(76px + env(safe-area-inset-bottom, 0px))' }}
          />
        </>
      )}
      <IntakePanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default IntakeEntryPoint;
